/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { Channel } from 'phoenix';
import { useCallback, useRef, useState } from 'react';

import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

import { useDownloadTenantReleaseMutation } from './mutation/useDownloadTenantReleaseMutation';

export type DownloadState =
  | { status: 'idle' }
  | { status: 'building' }
  | { status: 'ready'; url: string; fileSize: number }
  | { status: 'error'; message: string };

export type StartDownloadOptions = {
  debugBuild?: boolean;
  configType?: 'default' | 'scout_mode';
};

export function useInstallerDownload() {
  const socket = useMgmtPlaneStateStore((state) => state.socket);
  const [state, setState] = useState<DownloadState>({ status: 'idle' });
  const channelRef = useRef<Channel | null>(null);

  const downloadMutation = useDownloadTenantReleaseMutation();

  const startDownload = useCallback(
    async (releaseId: string, opts?: StartDownloadOptions) => {
      if (!socket) {
        setState({ status: 'error', message: 'WebSocket not connected' });
        return;
      }

      setState({ status: 'building' });

      try {
        const response = await downloadMutation.mutateAsync({
          releaseId,
          requestBody: {
            debugBuild: opts?.debugBuild ?? false,
            configType: opts?.configType ?? 'default',
          },
        });

        const {
          data: { downloadID },
        } = response;
        const ch = socket.channel(`download:${downloadID}`);

        ch.on('ready', (payload: { downloadUrl: string; fileSizeBytes: number }) => {
          setState({ status: 'ready', url: payload.downloadUrl, fileSize: payload.fileSizeBytes });
          window.location.href = payload.downloadUrl;
          ch.leave();
          channelRef.current = null;
        });

        ch.on('error', (payload: { message: string }) => {
          setState({ status: 'error', message: payload.message });
          ch.leave();
          channelRef.current = null;
        });

        ch.join()
          .receive('ok', () => {
            // Channel joined successfully, waiting for ready event
          })
          .receive('error', (resp: { reason: string }) => {
            setState({ status: 'error', message: `Channel join failed: ${resp.reason}` });
          });

        channelRef.current = ch;
      } catch (err) {
        setState({ status: 'error', message: `API error: ${(err as Error).message}` });
      }
    },
    [socket, downloadMutation],
  );

  const cleanup = useCallback(() => {
    channelRef.current?.leave();
    channelRef.current = null;
    setState({ status: 'idle' });
  }, []);

  return { state, startDownload, cleanup };
}
